import { Card, CardBody } from "reactstrap";
import useInternetConnection from '~/hooks/useInternetConnection';
import wifi from '~/assets/images/wifi-red.svg';
import infoSrc from '~/assets/images/instruction.svg';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { IMAGE_TYPES } from '~/constants/imageTypes';

interface CompleteVerificationProps {
  imageType: string;
  frontSrc: string;
  backSrc?: string;
  selfieSrc: string;
  startCapture: (imageType: string) => void;
  complete: () => void;
}

const CompleteVerification = ({ imageType, frontSrc, backSrc, selfieSrc, startCapture, complete }: CompleteVerificationProps) => {

  /********** STATE **********/
  // Use the hook to check the internet connection
  const { isConnectionSlow } = useInternetConnection(5000);

  /********** OTHER **********/
  const IMAGE_TYPE_DL_FRONT = 'DL_FRONT';
  const IMAGE_TYPE_DL_BACK = 'PDF417_BARCODE';
  const IMAGE_TYPE_SELFIE = 'SELFIE';
  const IMAGE_TYPE_PASSPORT = 'PASSPORT';

  // Determine if submit should be disabled based on image type
  const isSubmitDisabled = imageType === IMAGE_TYPES.DL_FRONT
    ? !frontSrc || !backSrc || !selfieSrc
    : !frontSrc || !selfieSrc;

  return <Card className="id-instructions">
    <CardBody>
      <div className="preview-row">
        <div className="preview-row-title mb-3">Everything looks good. Submit to complete verification.</div>
        <div className="d-flex justify-content-between">
          <Gallery options={{ mainClass: 'vid-request-gallery' }}>
            <div id="frontPreview" className="small-preview">
              <label className={!frontSrc ? 'success' : undefined}>{imageType === IMAGE_TYPES.DL_FRONT ? '1. Front of ID' : '1. Passport'}</label>
              <div className="image-wrapper">
                <div className='vid-image'>
                  <Item content={<div className='vid-image-preview'>
                    <img src={frontSrc} /></div>}>
                    {({ ref, open }) => (<img src={frontSrc} ref={ref} onClick={open} />)}
                  </Item>
                </div>
              </div>
              <button
                className="btn btn-success"
                onClick={() => startCapture(imageType === IMAGE_TYPES.DL_FRONT ? IMAGE_TYPE_DL_FRONT : IMAGE_TYPE_PASSPORT)}>
                Retake
              </button>
            </div>

            {imageType === IMAGE_TYPES.DL_FRONT && (
              <div id="backPreview" className="small-preview">
                <label className={!backSrc ? 'success' : undefined}>2. Back of ID</label>
                <div className="image-wrapper">
                  <div className='vid-image'>
                    <Item content={<div className='vid-image-preview'>
                      <img src={backSrc} /></div>}>
                      {({ ref, open }) => (<img src={backSrc} ref={ref} onClick={open} />)}
                    </Item>
                  </div>
                </div>
                <button className="btn btn-success" onClick={() => startCapture(IMAGE_TYPE_DL_BACK)}>Retake</button>
              </div>
            )}

            <div id="selfiePreview" className="small-preview">
              <label className={!selfieSrc ? 'success' : undefined}>{imageType === IMAGE_TYPES.DL_FRONT ? '3.' : '2.'} Selfie</label>
              <div className="image-wrapper">
                <div className='vid-image'>
                  <Item content={<div className='vid-image-preview'>
                    <img src={selfieSrc} /></div>}>
                    {({ ref, open }) => (<img src={selfieSrc} ref={ref} onClick={open} />)}
                  </Item>
                </div>
              </div>
              <button className="btn btn-success" onClick={() => startCapture(IMAGE_TYPE_SELFIE)}>Retake</button>
            </div>
          </Gallery>
        </div>
      </div>

      <div className="preview-row my-3">
        <img className="preview-row-icon" src={infoSrc} />
        <div className="preview-row-instruction">
          After you submit, your documents will be reviewed for verification. This usually takes less than 2 minutes.
        </div>
      </div>

      <div className="submit-wrapper">
        {isConnectionSlow &&
          <div className="wifi-message-box">
            <img src={wifi} className="mx-2" />
            Your internet connection doesn&apos;t seem stable and that can cause errors in the Identity Validation process.
            Please make sure you have a good internet connection before proceeding.
          </div>
        }
        <button
          className={'btn btn-primary w-100 submit-btn-mobile ' + `${isSubmitDisabled && 'disabled'}`}
          onClick={complete}
          disabled={isSubmitDisabled}
        >
          Submit
        </button>
      </div>
    </CardBody>
  </Card>
}

export default CompleteVerification;
